import { Chart, Plugin } from 'chart.js';

interface IPinchZoomState {
  startDistance: number | null;
  startXScalState: IChartXScalState | null;
  cleanup: () => void; // function to remove listeners
}

interface IChartXScalState {
  min: number;
  max: number;
  total: number;
  visible: number;
}

declare module 'chart.js' {
  interface Chart {
    _customBarPinchZoomState?: IPinchZoomState;
  }
}

export const customBarPinchZoomPlugin: Plugin = {
  id: 'customBarPinchZoomPlugin',

  afterInit(chart) {
    const canvas = chart.canvas as HTMLCanvasElement;

    const pinchState: IPinchZoomState = {
      startDistance: null,
      startXScalState: null,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      cleanup: () => {},
    };

    chart._customBarPinchZoomState = pinchState;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchState.startDistance = getDistance(e.touches);
        pinchState.startXScalState = getXScaleState(chart);
      }
    };

    const handleTouchEnd = () => {
      pinchState.startDistance = null;
      pinchState.startXScalState = null;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchState.startDistance && pinchState.startXScalState) {
        const touchCenter = getMidpointRelativeToChart(e.touches, chart);
        if (!touchCenter) return;

        const newDist = getDistance(e.touches);
        const scale = pinchState.startDistance / newDist;
        let newVisibleElements = Math.floor(pinchState.startXScalState.visible * scale);
        newVisibleElements = newVisibleElements < 1 ? 1 : newVisibleElements;

        const delta = newVisibleElements - pinchState.startXScalState.visible;
        const deltaX = Math.floor(delta * touchCenter?.x);
        const deltaY = delta - deltaX;

        let newMinIndex = pinchState.startXScalState.min - deltaX;
        let newMaxIndex = pinchState.startXScalState.max + deltaY;

        if (newMinIndex < 0) {
          newMinIndex = 0;
          newMaxIndex = newVisibleElements - 1;
        }

        if (newMaxIndex >= pinchState.startXScalState.total) {
          newMaxIndex = pinchState.startXScalState.total;
          newMinIndex = newMaxIndex - newVisibleElements + 1;
        }

        if (chart.options.scales && chart.options.scales['x']) {
          chart.options.scales['x'].min = newMinIndex;
          chart.options.scales['x'].max = newMaxIndex;
        }

        chart.update('zoom');
      }

      if (e.touches.length === 1 && pinchState.startDistance) {
        handleTouchEnd();
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    pinchState.cleanup = () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  },

  beforeDestroy(chart) {
    const state: IPinchZoomState | undefined = chart._customBarPinchZoomState;
    if (state && state.cleanup) {
      state.cleanup();
    }
  },
};

function getDistance(touches: TouchList): number {
  const [t1, t2] = [touches[0], touches[1]];

  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;

  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * y
 * |
 * 1 (max)
 * |
 * |        o {x: 0.5, y: 0.68}
 * |
 * |
 * |________________1(max)_x
 * 0
 */
function getMidpointRelativeToChart(touches: TouchList, chart: Chart): { x: number; y: number } | null {
  const [t1, t2] = [touches[0], touches[1]];
  const rect = chart.canvas.getBoundingClientRect();

  const { chartArea } = chart;
  if (!chartArea) return null;

  const chartAreaXStart = rect.left + chartArea.left;
  const chartAreaXEnd = chartAreaXStart + chartArea.width;
  const chartAreaYStart = rect.top + chartArea.top;
  const chartAreaYEnd = chartAreaYStart + chartArea.height;
  const touchScreenMidPositionX = (t1.clientX + t2.clientX) / 2;
  const touchScreenMidPositionY = (t1.clientY + t2.clientY) / 2;

  const midOnChartX =
    touchScreenMidPositionX > chartAreaXEnd
      ? chartAreaXEnd
      : touchScreenMidPositionX < chartAreaXStart
        ? chartAreaXStart
        : touchScreenMidPositionX;

  const midOnChartY =
    touchScreenMidPositionY > chartAreaYEnd
      ? chartAreaYEnd
      : touchScreenMidPositionY < chartAreaYStart
        ? chartAreaYStart
        : touchScreenMidPositionY;

  return {
    x: midOnChartX / chartArea.width,
    y: midOnChartY / chartArea.width,
  };
}

function getXScaleState(chart: Chart): IChartXScalState | null {
  const xScale = chart.scales['x'];
  if (!xScale) return null;

  const { min, max } = xScale;

  const total = chart.data.labels?.length ?? 0;
  const visible = (max || 0) - (min || 0) + 1;

  return { min, max, total, visible };
}
